import awsCronParser from "aws-cron-parser";
import * as chrono from "chrono-node";
import { DateTime, Duration } from "luxon";

/** The scheduler's rounding step. */
const QUARTER_HOUR = Duration.fromObject({ minutes: 15 });

/**
 * The scheduler's default send time: `date` moved up to a quarter hour.
 *
 * Ported unchanged from date-fns 2's `roundToNearestMinutes` with `nearestTo: 15` and `roundingMethod: "ceil"`, which
 * adds the rounded remainder on top of the ceiling: from 7.5 minutes past a quarter onward it lands on the quarter after
 * the next one (10:08 becomes 10:30, not 10:15).
 */
export const roundUpToQuarterHour = (date: Date): Date => {
  const viewer = DateTime.fromJSDate(date);
  if (!viewer.isValid) {
    return new Date(Number.NaN);
  }
  // Minutes into the hour, seconds included and milliseconds dropped, as date-fns read them.
  const minutes = Duration.fromObject({ minutes: viewer.minute, seconds: viewer.second }).as("minutes");
  const step = QUARTER_HOUR.as("minutes");
  const ceiling = Math.ceil(minutes / step) * step;
  const remainder = Math.round((minutes % step) / step) * step;
  return firstOccurrence(viewer.set({ minute: ceiling + remainder, second: 0, millisecond: 0 })).toJSDate();
};

/**
 * The day of the week as the scheduler numbers it, Sunday first: 1 is Sunday and 7 is Saturday.
 */
export const sundayFirstDayNumber = (date: Date | null | undefined): number =>
  date ? sundayFirstDayIndex(DateTime.fromJSDate(date)) + 1 : Number.NaN;

/**
 * `date` moved to another day of its own Sunday-first week, keeping its clock; `day` counts as `Date#getDay` does, 0
 * for Sunday.
 */
export const withDayOfWeek = (date: Date | null, day: number): Date => {
  const viewer = date ? DateTime.fromJSDate(date) : null;
  if (!viewer?.isValid) {
    return new Date(Number.NaN);
  }
  // Staying on the same day keeps the instant itself, as date-fns did; a move reads its new day's clock afresh.
  const shift = day - sundayFirstDayIndex(viewer);
  return shift === 0 ? viewer.toJSDate() : firstOccurrence(viewer.plus({ days: shift })).toJSDate();
};

/**
 * `dateTime` at the first occurrence of its wall-clock time, as `Date` resolves a time the fall-back hour repeats.
 *
 * Luxon keeps the offset a calculation started from, which can land on the second occurrence instead.
 */
export function firstOccurrence(dateTime: DateTime): DateTime {
  return DateTime.min(...dateTime.getPossibleOffsets()) ?? dateTime;
}

/**
 * The day of the week counted as `Date#getDay` counts it, 0 for Sunday; Luxon counts Monday 1 through Sunday 7.
 */
function sundayFirstDayIndex(dateTime: DateTime): number {
  return dateTime.weekday % 7;
}

const getFeedDateId = (date: string) => `feed-date-${date}`;

export const parseCronScheduledDate = (cronStr: string, timezone: string) => {
  const cron = awsCronParser.parse(cronStr);
  if (cron) {
    // Minutes east of UTC; an empty zone reads as UTC, as date-fns-tz read it.
    const offset = timezone ? DateTime.now().setZone(timezone).offset : 0;
    const parsedDate = chrono.parseDate(
      awsCronParser.getScheduleDescription(cron),
      {
        instant: new Date(),
        timezone: offset,
      },
      { forwardDate: true },
    );
    return parsedDate;
  }
  return null;
};

export type TimeDifference = {
  targetDate: Date;
  now: Date;
  diffInMilliseconds: number;
  diffInMinutesAbsolute: number;
  diffInSeconds: number;
  diffInMinutes: number;
  diffInHours: number;
  diffInDays: number;
  lastOnline: string;
  stale: boolean;
};

export const dateDiff = (timestamp: Date | string, minutesThreshold: number): TimeDifference => {
  const targetDate = new Date(timestamp);
  const now = new Date();
  // Elapsed time in each unit, at Luxon's fixed 24-hour day, 7-day week, and 30-day month.
  const diff = DateTime.fromJSDate(now).diff(DateTime.fromJSDate(targetDate));
  const elapsedIn = (unit: "milliseconds" | "seconds" | "minutes" | "hours" | "days" | "weeks" | "months") =>
    Math.abs(diff.as(unit));
  const diffInMilliseconds = elapsedIn("milliseconds");
  const diffInSeconds = Math.floor(elapsedIn("seconds"));
  const diffInMinutes = Math.floor(elapsedIn("minutes"));
  const diffInHours = Math.floor(elapsedIn("hours"));
  const diffInDays = Math.floor(elapsedIn("days"));
  const diffInWeeks = Math.floor(elapsedIn("weeks"));
  const diffInMonths = Math.floor(elapsedIn("months"));
  // Years count twelve whole 30-day months, not 365 days.
  const diffInYears = Math.floor(diffInMonths / Duration.fromObject({ years: 1 }).as("months"));

  let lastOnline = "";
  if (diffInYears > 0) {
    lastOnline = `${diffInYears}y`;
  } else if (diffInMonths > 0) {
    lastOnline = `${diffInMonths}mo`;
  } else if (diffInWeeks > 0) {
    lastOnline = `${diffInWeeks}w`;
  } else if (diffInDays > 0) {
    lastOnline = `${diffInDays}d`;
  } else if (diffInHours > 0) {
    lastOnline = `${diffInHours}h`;
  } else if (diffInMinutes >= minutesThreshold) {
    lastOnline = `${diffInMinutes}m`;
  }

  const diffInMinutesAbsolute = elapsedIn("minutes");
  const stale = diffInMinutesAbsolute > minutesThreshold;

  return {
    targetDate,
    now,
    diffInMilliseconds,
    diffInMinutesAbsolute,
    diffInSeconds,
    diffInMinutes,
    diffInHours,
    diffInDays,
    lastOnline,
    stale,
  };
};
